import React from 'react';
import Container from '@mui/material/Container';
import { Image } from 'mui-image';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

export default function Certifications() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const sectionBg = isDark ? '#0d1117' : '#f0f6fc';
  const cardBg = isDark ? '#161b22' : '#ffffff';
  const primaryText = isDark ? '#f0f6fc' : '#0d1117';
  const secondaryText = isDark ? '#c9d1d9' : '#555555';
  const accentRed = '#ff0033';
  const accentCyan = '#00ffff';

  const certs = [
    {
      title: "CCNA (Cisco Certified Network Associate)",
      desc: "Proven core knowledge in network fundamentals, IP connectivity, security parameters, and automation. This helps me design and deploy highly secure web application infrastructures.",
      credly: "https://www.credly.com/users/nicholas-murimi/badges#credly",
      image: "https://unsplash.com",
      imgHeight: "260px" 
    },
    {
      title: "AI in Software Development",
      desc: "Expertise in integrating large language models, building AI agents, and using advanced semantic tools to speed up coding pipelines and automate software workflows.",
      credly: "https://www.credly.com/users/nicholas-murimi/badges#credly",
      image: "https://unsplash.com",
      imgHeight: "160px" 
    },
    {
      title: "Next.js Application Development",
      desc: "Deep understanding of production-ready React frameworks, Server-Side Rendering (SSR), Static Site Generation (SSG), and building optimized, SEO-friendly web architectures.",
      credly: "https://www.credly.com/users/nicholas-murimi/badges#credly",
      image: "https://unsplash.com",
      imgHeight: "210px" 
    }
  ];

  return (
    <Box
      component="section"
      id="certifications"
      sx={{ bgcolor: sectionBg, py: 10 }}
    >
      <Container maxWidth="lg">
        {/* Section Title */}
        <Typography
          variant="h2"
          sx={{
            fontFamily: "JetBrains Mono, monospace",
            fontSize: { xs: "32px", md: "48px" },
            fontWeight: 700,
            color: accentRed,
            mb: 6,
            textAlign: "center",
          }}
        >
          <Box component="span" sx={{ color: accentCyan }}>
            $
          </Box>{" "}
          credentials
        </Typography>

        {/* Pinterest-Style CSS Grid Container */}
        <Box 
          sx={{ 
            display: 'grid',
            gap: 3,
            gridTemplateColumns: {
              xs: '1fr',          // 1 column on mobile
              sm: '1fr 1fr',      // 2 columns on tablets
              md: '1fr 1fr 1fr'   // 3 columns on desktops
            },
            alignItems: 'start',  // Keeps items from stretching vertically
            mb: 6 
          }}
        >
          {certs.map((cert, index) => (
            <Box
              key={index}
              sx={{
                bgcolor: cardBg,
                borderRadius: "16px", 
                overflow: "hidden",
                borderLeft: `4px solid ${index === 0 ? accentRed : accentCyan}`,
                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.05)",
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.02)" 
                }
              }}
            >
              {/* Visual Image Header */}
              <Image
                src={cert.image}
                alt={cert.title}
                fit="cover"
                style={{
                  width: "100%",
                  height: cert.imgHeight, 
                  objectFit: "cover",
                }}
              />

              {/* Card Content Details */}
              <Box sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontWeight: 700,
                    color: primaryText,
                    mb: 1,
                  }}
                >
                  {cert.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    lineHeight: 1.6,
                    color: secondaryText,
                    mb: 2,
                  }}
                >
                  {cert.desc}
                </Typography>
                <Button
                  component="a"
                  href={cert.credly}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  variant="text"
                  endIcon={<Icon>arrow_forward</Icon>}
                  sx={{
                    color: accentCyan,
                    fontFamily: "JetBrains Mono, monospace",
                    textTransform: "none",
                    p: 0,
                    "&:hover": {
                      color: accentRed,
                      bgcolor: "transparent",
                    },
                  }}
                >
                  view on credly
                </Button>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Centered Action Button */}
        <Box sx={{ maxWidth: '350px', mx: 'auto' }}>
          <Button
            component="a"
            href="/resume.pdf"
            download="resume.pdf"
            variant="outlined"
            fullWidth
            startIcon={<Icon>download</Icon>}
            sx={{
              borderColor: accentCyan,
              color: accentCyan,
              fontFamily: "JetBrains Mono, monospace",
              py: 1.5,
              "&:hover": {
                borderColor: accentCyan,
                bgcolor: `rgba(0, 255, 255, 0.1)`,
              },
            }}
          >
            $ download resume.pdf
          </Button>
        </Box>
      </Container>
    </Box>
  );
}


